import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  ConciergePassAccess,
  ExpectedResponse,
  PassAction,
  PassListResponse,
  PassSpecInput,
  VisitorPass,
  VisitorPassDetail,
} from '../interfaces/visitor-pass';

/**
 * VISITOR PASSES — the STAFF client (resident-portal Plan 2).
 *
 * Staff routes only (lobbero_be/src/CRUD/visitor-passes.js + the one tap in
 * visitor-log.js). A resident never calls these: their passes go through the
 * /tenant/passes routes of Plans 3–4.
 *
 * Uses the DEFAULT axios instance on purpose, so every call inherits the
 * interceptors from axios-setup.ts: withCredentials for the httpOnly session
 * cookies, the X-CSRF-Token double-submit header, and the single-flight
 * 401 -> refresh -> retry.
 *
 * The building's zone is the server's: there is deliberately no `tz` parameter
 * anywhere here — the server ignores one.
 */

export const listExpected = async (
  buildingId: number,
  date?: string
): Promise<ExpectedResponse> => {
  const { data } = await axios.get(
    `${apiRoute}/buildings/${buildingId}/expected`,
    { params: date ? { date } : {} }
  );

  return data as ExpectedResponse;
};

export const listPasses = async (
  buildingId: number,
  opts: {
    state?: 'live' | 'ended' | 'all' | 'awaiting';
    unitId?: number;
    cursor?: string | null;
    limit?: number;
  } = {}
): Promise<PassListResponse> => {
  const { data } = await axios.get(
    `${apiRoute}/buildings/${buildingId}/passes`,
    {
      params: {
        state: opts.state ?? 'live',
        ...(opts.unitId != null ? { unit_id: opts.unitId } : {}),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.limit ? { limit: opts.limit } : {}),
      },
    }
  );

  return data as PassListResponse;
};

export const getPass = async (passId: string): Promise<VisitorPassDetail> => {
  const { data } = await axios.get(`${apiRoute}/passes/${passId}`);

  return data as VisitorPassDetail;
};

export const createPass = async (
  buildingId: number,
  hostOccupancyId: string,
  spec: PassSpecInput
): Promise<VisitorPass> => {
  const { data } = await axios.post(
    `${apiRoute}/buildings/${buildingId}/passes`,
    { ...spec, host_occupancy_id: hostOccupancyId }
  );

  return data as VisitorPass;
};

export const updatePass = async (
  passId: string,
  spec: PassSpecInput
): Promise<VisitorPass> => {
  const { data } = await axios.patch(`${apiRoute}/passes/${passId}`, spec);

  return data as VisitorPass;
};

export const passAction = async (
  passId: string,
  action: PassAction,
  extra: { date?: string; until?: string | null } = {}
): Promise<VisitorPass> => {
  const { data } = await axios.post(`${apiRoute}/passes/${passId}/actions`, {
    action,
    ...extra,
  });

  return data as VisitorPass;
};

/**
 * APPROVAL MODE (migration 125, RP-4.9): a manager or the owner answers a
 * resident's pending pass. Approve -> active; decline -> ended, with an
 * optional note the household reads on the pass.
 */
export const decidePass = async (
  passId: string,
  decision: 'approve' | 'reject',
  note?: string,
  /** The pass's updated_at as the screen showed it. A pending pass stays
   *  editable by its household: when it changed in the meantime the server
   *  decides nothing and answers 409 PASS_CHANGED (Plan 8). */
  seenUpdatedAt?: string | null
): Promise<VisitorPass> => {
  const { data } = await axios.post(`${apiRoute}/passes/${passId}/decision`, {
    decision,
    ...(note && note.trim() ? { note: note.trim() } : {}),
    ...(seenUpdatedAt ? { seen_updated_at: seenUpdatedAt } : {}),
  });

  return data as VisitorPass;
};

// ---------------------------------------------------------------------------
// THE BUILDING'S PASS SETTINGS — PUT /buildings/:b/visitor-passes (managers
// and owners). `concierge_access` is how far the front desk may write passes
// here (migration 118); `approval` whether a manager approves every pass a
// resident adds (migration 125).
// ---------------------------------------------------------------------------

export interface PassSettings {
  building_id: number;
  visitor_passes_enabled: boolean;
  concierge_pass_access: ConciergePassAccess;
  visitor_pass_approval?: boolean;
}

export const updatePassSettings = async (
  buildingId: number,
  change: { enabled?: boolean; concierge_access?: ConciergePassAccess; approval?: boolean }
): Promise<PassSettings> => {
  const { data } = await axios.put(`${apiRoute}/buildings/${buildingId}/visitor-passes`, change);

  return data as PassSettings;
};

// ---------------------------------------------------------------------------
// THE ONE TAP — POST /visitor-log/from-pass.
// ---------------------------------------------------------------------------

export type SignInFromPassCode =
  | 'PASS_NOT_TODAY'
  | 'PASS_OUTSIDE_WINDOW'
  | 'ALREADY_INSIDE'
  | 'PASSES_DISABLED'
  | 'PASS_NOT_FOUND'
  | 'SUBSCRIPTION_ENDED'
  // Plan 6: a scanned pass link that no longer works, or names another building.
  | 'PASS_LINK_NOT_VALID'
  | 'PASS_OTHER_BUILDING';

export class SignInFromPassError extends Error {
  /** null when the request never reached the server. */
  readonly status: number | null;

  /** The rule the server named, when it named one. */
  readonly code: SignInFromPassCode | null;

  constructor(message: string, status: number | null, code: SignInFromPassCode | null) {
    super(message);
    this.name = 'SignInFromPassError';
    this.status = status;
    this.code = code;
  }
}

export interface SignInFromPassResult {
  id: number;
  window_state: 'in_window' | 'early' | 'late';
  createdAt: string;
  /** How many were actually WRITTEN. The server clamps the party to what the
   *  pass has left (another tablet may have signed some in meanwhile), so this
   *  can be fewer than was asked for — the desk is told (RP-9.7). */
  numberOfGuests?: number;
}

export const signInFromPass = async (input: {
  passId: string;
  buildingId: number;
  numberOfGuests?: number;
  notes?: string;
  confirmOutsideWindow?: boolean;
}): Promise<SignInFromPassResult> => {
  try {
    const { data } = await axios.post(`${apiRoute}/visitor-log/from-pass`, {
      pass_id: input.passId,
      building_id: input.buildingId,
      ...(input.numberOfGuests ? { number_of_guests: input.numberOfGuests } : {}),
      ...(input.notes ? { notes: input.notes } : {}),
      ...(input.confirmOutsideWindow ? { confirm_outside_window: true } : {}),
    });

    return data as SignInFromPassResult;
  } catch (e) {
    const err = e as AxiosError<{ message?: string; code?: SignInFromPassCode }>;
    const status = err.response?.status ?? null;
    const code = err.response?.data?.code ?? null;

    throw new SignInFromPassError(
      status === 402
        ? 'Your building’s Lobbero subscription is inactive.'
        : 'Could not sign the visitor in.',
      status,
      code
    );
  }
};

// ---------------------------------------------------------------------------
// THE SCAN (Plan 6, RP-6.4). The Expected screen's camera decodes a guest's pass
// QR ON THE TABLET; these two calls are the only server traffic it causes.
// ---------------------------------------------------------------------------

/**
 * Which pass a scanned token names, at THIS building. 404 PASS_LINK_NOT_VALID
 * (revoked, ended, pending, unknown) or PASS_OTHER_BUILDING — thrown as a
 * SignInFromPassError so the screen reads one error shape.
 */
export const resolvePassLink = async (
  buildingId: number,
  token: string
): Promise<{ pass_id: string }> => {
  try {
    const { data } = await axios.post(
      `${apiRoute}/buildings/${buildingId}/pass-links/resolve`,
      { token }
    );

    return { pass_id: String(data?.pass_id || '') };
  } catch (e) {
    const err = e as AxiosError<{ message?: string; code?: SignInFromPassCode }>;

    throw new SignInFromPassError(
      'Could not read that pass.',
      err.response?.status ?? null,
      err.response?.data?.code ?? null
    );
  }
};

/**
 * The one tap, from a SCANNED pass: POST /visitor-log/from-pass-token. Every
 * rule of the tap applies unchanged on the server (signInFromPass); the token
 * stands in for the pass id.
 */
export const signInFromPassToken = async (input: {
  token: string;
  buildingId: number;
  numberOfGuests?: number;
  notes?: string;
  confirmOutsideWindow?: boolean;
}): Promise<SignInFromPassResult> => {
  try {
    const { data } = await axios.post(`${apiRoute}/visitor-log/from-pass-token`, {
      token: input.token,
      building_id: input.buildingId,
      ...(input.numberOfGuests ? { number_of_guests: input.numberOfGuests } : {}),
      ...(input.notes ? { notes: input.notes } : {}),
      ...(input.confirmOutsideWindow ? { confirm_outside_window: true } : {}),
    });

    return data as SignInFromPassResult;
  } catch (e) {
    const err = e as AxiosError<{ message?: string; code?: SignInFromPassCode }>;
    const status = err.response?.status ?? null;

    throw new SignInFromPassError(
      status === 402
        ? 'Your building’s Lobbero subscription is inactive.'
        : 'Could not sign the visitor in.',
      status,
      err.response?.data?.code ?? null
    );
  }
};
